/**
 * MonthView — 월 단위 그리드 뷰
 *
 * 7 × 6 셀. 종일/시간 지정 분기 (Google Calendar 패턴).
 * event_type 폐기 후: 색상은 primary 단일 + event.color override 만 사용.
 */
import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { CalendarEvent, EventChipStyle } from '../../../models/CalendarEvent';
import { cn } from '../../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';
import ChipMarkerIcon from './ChipMarkerIcon';
import { weekdayLabels } from '../calendarWeekdays';
import { useCalendarLabels } from '../useCalendarLabels';

interface Props {
  anchorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent, e?: React.MouseEvent) => void;
  onCellClick?: (date: Date) => void;
  /** 일정 → 시각 스타일 함수 (카테고리 기반) */
  getStyle: (event: CalendarEvent) => EventChipStyle;
}


function buildMonthCells(anchor: Date): Date[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - first.getDay());
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function isEventOnDate(event: CalendarEvent, date: Date): boolean {
  const start = new Date(event.start_at);
  // 시간 일정은 시작일 기준 1일에만 표시 — 자정을 넘겨 끝나도(예: 23:30~익일 00:30) 시작일에만.
  if (!event.is_all_day) {
    return sameDay(start, date);
  }
  // 종일 일정은 기간 전체에 걸쳐 표시 (multi-day 바)
  const end = new Date(event.end_at);
  const d0 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
  return start < d1 && end > d0;
}

const MonthView: React.FC<Props> = ({ anchorDate, events, onEventClick, onCellClick, getStyle }) => {
  const labels = useCalendarLabels();
  const cells = useMemo(() => buildMonthCells(anchorDate), [anchorDate]);
  const today = new Date();
  const currentMonth = anchorDate.getMonth();
  const weekdays = weekdayLabels();

  // 셀 높이에 맞춰 표시 가능한 일정 개수(capacity)를 동적 계산.
  // 그리드 행 높이(= 셀 높이) ÷ 칩 높이 — 실제 렌더된 DOM 을 측정해 브레이크포인트/셀 크기에 자동 적응.
  const gridRef = useRef<HTMLDivElement>(null);
  const [capacity, setCapacity] = useState(4);
  const [moreOpen, setMoreOpen] = useState<string | null>(null); // "+N 더보기" 팝오버가 열린 날짜 키

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const rowH = grid.clientHeight / 6;
      if (rowH < 1) return;
      const cell = grid.querySelector('[data-cell]') as HTMLElement | null;
      const evBox = cell?.querySelector('[data-events]') as HTMLElement | null;
      let reserved = 40; // 날짜 영역 + 상/하 패딩 (측정 실패 시 fallback)
      let gap = 2;
      if (cell && evBox) {
        const cs = getComputedStyle(cell);
        reserved = (evBox.getBoundingClientRect().top - cell.getBoundingClientRect().top)
          + parseFloat(cs.paddingBottom || '0');
        gap = parseFloat(getComputedStyle(evBox).rowGap || '2') || 2;
      }
      const chip = grid.querySelector('[data-chip]') as HTMLElement | null;
      const chipH = chip?.offsetHeight || 20;
      const cap = Math.max(1, Math.floor((rowH - reserved) / (chipH + gap)));
      setCapacity((prev) => (prev === cap ? prev : cap));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [events]);

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 border-b border-border">
        {weekdays.map((label, idx) => (
          <div
            key={label}
            className={cn(
              'h-8 flex items-center justify-center text-caption font-medium text-muted-foreground',
              idx === 0 && 'text-rose-500',
              idx === 6 && 'text-sky-500',
            )}
          >
            {label}
          </div>
        ))}
      </div>

      <div ref={gridRef} className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0">
        {cells.map((date, idx) => {
          const isCurrentMonth = date.getMonth() === currentMonth;
          const isToday = sameDay(date, today);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const dayAll = events.filter((e) => isEventOnDate(e, date));
          const dayEvents = dayAll.length > capacity ? dayAll.slice(0, Math.max(0, capacity - 1)) : dayAll;
          const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

          return (
            <div
              key={idx}
              data-cell
              onClick={() => {
                // 월 뷰엔 시간 개념이 없으므로 클릭 시 현재 시각을 반영해 새 일정을 연다 (Google Calendar 방식)
                const now = new Date();
                const withTime = new Date(date);
                withTime.setHours(now.getHours(), now.getMinutes(), 0, 0);
                onCellClick?.(withTime);
              }}
              className={cn(
                'flex flex-col gap-0.5 sm:gap-1 p-0.5 sm:p-1.5 border-r border-b border-border min-h-0 overflow-hidden transition-colors',
                onCellClick && 'cursor-pointer hover:bg-muted/40',
                !isCurrentMonth && 'bg-muted/30',
                (idx + 1) % 7 === 0 && 'border-r-0',
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center size-6 rounded-full text-caption font-medium shrink-0',
                  isToday && 'bg-primary text-primary-foreground',
                  !isToday && !isCurrentMonth && 'text-muted-foreground/60',
                  !isToday && isCurrentMonth && date.getDay() === 0 && 'text-rose-500',
                  !isToday && isCurrentMonth && date.getDay() === 6 && 'text-sky-500',
                  !isToday && isCurrentMonth && !isWeekend && 'text-foreground',
                )}
              >
                {date.getDate()}
              </div>

              {/* 종일 = 컬러 막대 / 시간 지정 = dot + 시간 + 제목 */}
              <div data-events className="flex flex-col gap-0.5 min-h-0 overflow-hidden">
                {dayEvents.map((event) => {
                  const style = getStyle(event);
                  const categoryLabel = labels.category(style.category);
                  if (event.is_all_day) {
                    return (
                      <div
                        key={event.id}
                        data-chip
                        onClick={(e) => { e.stopPropagation(); onEventClick?.(event, e); }}
                        className={cn(
                          'flex items-center gap-0.5 sm:gap-1 text-micro sm:text-meta font-medium px-1 py-0 sm:px-1.5 sm:py-0.5 rounded sm:rounded-md cursor-pointer hover:opacity-80 min-w-0 leading-tight',
                          style.filledBg,
                        )}
                        style={event.color ? { backgroundColor: event.color, color: '#fff' } : undefined}
                        title={`${categoryLabel} · ${event.title}`}
                      >
                        <ChipMarkerIcon marker={style.marker} className="size-3 hidden sm:block" />
                        <span className="truncate">{event.title}</span>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={event.id}
                      data-chip
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event, e); }}
                      className={cn(
                        'flex items-center gap-0.5 sm:gap-1 text-micro sm:text-meta px-0.5 py-0 sm:px-1 sm:py-0.5 rounded sm:rounded-md cursor-pointer hover:bg-muted/60 min-w-0 leading-tight',
                        'sm:border-0',
                        style.borderExtra && `sm:border ${style.borderExtra}`,
                      )}
                      title={`${categoryLabel} · ${event.title}`}
                    >
                      <ChipMarkerIcon marker={style.marker} className={cn('size-3 hidden sm:block', style.markerColor)} />
                      <span className="truncate text-foreground">{event.title}</span>
                    </div>
                  );
                })}
                {dayAll.length > dayEvents.length && (
                  // modal: 바깥 클릭이 셀(onCellClick=새 일정)로 전파되지 않고 팝오버만 닫히도록
                  <Popover open={moreOpen === dayKey} onOpenChange={(o) => setMoreOpen(o ? dayKey : null)} modal>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="text-left text-micro sm:text-meta text-muted-foreground px-0.5 sm:px-1.5 leading-tight cursor-pointer hover:text-foreground hover:bg-muted/60 rounded"
                      >
                        <Trans>+{dayAll.length - dayEvents.length} more</Trans>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 p-0" onClick={(e) => e.stopPropagation()}>
                      <div className="px-3 py-2 border-b border-border text-label font-medium text-foreground">
                        {date.toLocaleDateString(i18n.locale || 'en', { month: 'long', day: 'numeric', weekday: 'short' })}
                      </div>
                      <div className="max-h-72 overflow-y-auto py-1">
                        {dayAll.map((event) => {
                          const st = getStyle(event);
                          const categoryLabel = labels.category(st.category);
                          const time = event.is_all_day
                            ? null
                            : new Date(event.start_at).toLocaleTimeString(i18n.locale || 'en', { hour: '2-digit', minute: '2-digit', hour12: false });
                          return (
                            <button
                              key={event.id}
                              type="button"
                              onClick={(e) => { setMoreOpen(null); onEventClick?.(event, e); }}
                              className="flex items-center gap-1.5 w-full text-left px-3 py-1.5 hover:bg-muted/60 min-w-0"
                              title={`${categoryLabel} · ${event.title}`}
                            >
                              <ChipMarkerIcon marker={st.marker} className={cn('size-3 shrink-0', st.markerColor)} />
                              {time && <span className="text-caption text-muted-foreground tabular-nums shrink-0">{time}</span>}
                              <span className="text-label text-foreground truncate">{event.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 로딩 코너 텍스트 제거 — 모바일 2행 헤더에서 ✏️ 버튼과 겹침. 이전 데이터 유지(stale-while-revalidate)로 충분 */}
    </div>
  );
};

export default MonthView;
